import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import type { Metric } from '@/content/content';

interface MetricReadoutProps {
  metric: Metric;
  /** Stagger index — later readouts start counting slightly later. */
  index?: number;
}

const COUNT_MS = 1100;

/** Ease-out cubic — decelerates into the final reading like a settling gauge. */
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * A single instrument readout: the value counting up on load, plus its
 * label/caption. Count-up is skipped under prefers-reduced-motion (the final
 * value renders immediately).
 */
const MetricReadout = ({ metric, index = 0 }: MetricReadoutProps): JSX.Element => {
  const prefersReduced = useReducedMotion();
  const [value, setValue] = useState<number>(prefersReduced ? metric.to : 0);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    if (prefersReduced) {
      setValue(metric.to);
      return;
    }

    const delay = index * 120;
    const start = performance.now() + delay;

    const tick = (now: number): void => {
      const elapsed = now - start;
      if (elapsed <= 0) {
        frameRef.current = requestAnimationFrame(tick);
        return;
      }
      const progress = Math.min(elapsed / COUNT_MS, 1);
      setValue(metric.to * easeOut(progress));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [metric.to, index, prefersReduced]);

  const display = value.toFixed(metric.decimals);
  const arrow = metric.dir === 'up' ? '↑' : '↓';

  return (
    <div className="flex flex-col gap-2 px-4 py-5 sm:px-5 sm:py-6">
      <p className="font-mono text-3xl font-semibold tabular-nums text-primary-ink dark:text-primary sm:text-[2.5rem] sm:leading-none">
        {metric.prefix}
        {display}
        {metric.suffix}
        <span className="ml-1 align-super text-sm text-[color:var(--fg-muted)]" aria-hidden="true">
          {arrow}
        </span>
      </p>
      <p className="text-sm font-semibold leading-snug text-[color:var(--fg)]">{metric.label}</p>
      <p className="eyebrow !tracking-[0.12em]">{metric.caption}</p>
    </div>
  );
};

export default MetricReadout;
